/**
 * 分支树组件
 *
 * 显示工作区的分支树结构，支持：
 * - 递归显示分支层级
 * - 分支状态标识
 * - 展开/折叠功能
 * - 分支切换
 * - 进度统计显示
 * - 拖拽排序功能
 */

import React, { useEffect, useCallback, useRef, useState } from 'react';
import type { Branch, BranchTreeNode } from '../../types/branch';
import { useBranchStore } from '../../store/branchStore';
import { BranchNode } from './BranchNode';

// ==================== SVG 图标 ====================

const ICONS = {
  refresh: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
  expandAll: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
    </svg>
  ),
  collapseAll: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
    </svg>
  ),
  mainLine: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
    </svg>
  ),
  empty: (
    <svg className="w-16 h-16 mx-auto mb-3 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  error: (
    <svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  loadingLarge: <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />,
  loadingSmall: <div className="inline-block animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500" />,
};

// ==================== 主线节点组件 ====================

interface MainLineNodeProps {
  isSelected: boolean;
  isCurrentBranch: boolean;
  onClick: () => void;
  onDoubleClick: () => void;
}

const MainLineNode: React.FC<MainLineNodeProps> = ({
  isSelected,
  isCurrentBranch,
  onClick,
  onDoubleClick,
}) => {
  return (
    <div
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      className={`
        flex items-center gap-2 py-2 px-3 rounded-lg cursor-pointer
        transition-all duration-150
        ${isSelected
          ? 'bg-blue-50 border-l-4 border-blue-500'
          : 'hover:bg-gray-50 border-l-4 border-transparent'
        }
        ${isCurrentBranch ? 'ring-2 ring-blue-200' : ''}
      `}
    >
      <div className="flex-shrink-0 text-blue-500">
        {ICONS.mainLine}
      </div>
      <span className={`font-medium ${isSelected ? 'text-blue-700' : 'text-gray-900'}`}>
        主线剧情
      </span>
      {isCurrentBranch && (
        <span className="px-1.5 py-0.5 text-xs bg-blue-100 text-blue-700 rounded">
          当前
        </span>
      )}
    </div>
  );
};

// ==================== 主组件 ====================

interface BranchTreeProps {
  /** 工作区ID */
  workspaceId: number;
  /** 分支选中回调 */
  onBranchSelect?: (branch: Branch | null) => void;
  /** 分支切换回调 */
  onBranchSwitch?: (branchId: number | null) => void;
  /** 是否显示工具栏 */
  showToolbar?: boolean;
}

export const BranchTree: React.FC<BranchTreeProps> = ({
  workspaceId,
  onBranchSelect,
  onBranchSwitch,
  showToolbar = true,
}) => {
  // 从 store 获取状态
  const {
    branchTree,
    loading,
    error,
    isMainLine,
    selectedBranchId,
    fetchBranchTree,
    setExpandedBranchIds,
    setSelectedBranchId,
    reorderBranches,
  } = useBranchStore();

  // 首次加载标记
  const hasInitiallyLoadedRef = useRef(false);
  const isRefreshingRef = useRef(false);

  // 拖拽状态
  const [draggingBranch, setDraggingBranch] = useState<Branch | null>(null);
  const [dragTarget, setDragTarget] = useState<{ branchId: number; position: 'before' | 'after' | 'inside' } | null>(null);

  // 加载分支树
  const loadBranchTree = useCallback(async () => {
    if (!workspaceId || isRefreshingRef.current) return;

    isRefreshingRef.current = true;
    try {
      await fetchBranchTree(workspaceId);
    } finally {
      isRefreshingRef.current = false;
    }
  }, [workspaceId, fetchBranchTree]);

  // 首次加载
  useEffect(() => {
    if (!hasInitiallyLoadedRef.current && workspaceId) {
      hasInitiallyLoadedRef.current = true;
      loadBranchTree();
    }
  }, [workspaceId, loadBranchTree]);

  // 刷新
  const handleRefresh = useCallback(() => {
    loadBranchTree();
  }, [loadBranchTree]);

  // 展开所有分支
  const handleExpandAll = useCallback(() => {
    const allBranchIds = new Set<number>();
    const collectIds = (nodes: BranchTreeNode[]) => {
      nodes.forEach((node) => {
        allBranchIds.add(node.branch.id);
        if (node.children) {
          collectIds(node.children);
        }
      });
    };
    collectIds(branchTree);
    setExpandedBranchIds(allBranchIds);
  }, [branchTree, setExpandedBranchIds]);

  // 折叠所有分支
  const handleCollapseAll = useCallback(() => {
    setExpandedBranchIds(new Set<number>());
  }, [setExpandedBranchIds]);

  // 处理主线点击
  const handleMainLineClick = useCallback(() => {
    setSelectedBranchId(null);
    onBranchSelect?.(null);
  }, [setSelectedBranchId, onBranchSelect]);

  // 处理主线双击（切换到主线）
  const handleMainLineDoubleClick = useCallback(() => {
    onBranchSwitch?.(null);
  }, [onBranchSwitch]);

  // 处理分支点击
  const handleBranchClick = useCallback((branch: Branch) => {
    setSelectedBranchId(branch.id);
    onBranchSelect?.(branch);
  }, [setSelectedBranchId, onBranchSelect]);

  // 处理分支双击（切换分支）
  const handleBranchSwitch = useCallback((branchId: number | null) => {
    onBranchSwitch?.(branchId);
  }, [onBranchSwitch]);

  // 拖拽开始
  const handleDragStart = useCallback((branch: Branch) => {
    setDraggingBranch(branch);
    setDragTarget(null);
  }, []);

  // 拖拽结束
  const handleDragEnd = useCallback(() => {
    setDraggingBranch(null);
    setDragTarget(null);
  }, []);

  // 拖拽经过
  const handleDragOver = useCallback((branch: Branch, position: 'before' | 'after' | 'inside') => {
    setDragTarget({ branchId: branch.id, position });
  }, []);

  // 放置处理
  const handleDrop = useCallback(async (targetBranch: Branch, position: 'before' | 'after' | 'inside') => {
    if (!draggingBranch || !workspaceId) {
      setDraggingBranch(null);
      setDragTarget(null);
      return;
    }

    // 不能拖到自己身上
    if (draggingBranch.id === targetBranch.id) {
      setDraggingBranch(null);
      setDragTarget(null);
      return;
    }

    // 如果是 inside，暂不支持嵌套
    if (position === 'inside') {
      setDraggingBranch(null);
      setDragTarget(null);
      return;
    }

    try {
      // 收集同级分支用于排序
      const collectSiblings = (
        nodes: BranchTreeNode[],
        targetId: number
      ): { siblings: BranchTreeNode[]; targetIndex: number } | null => {
        for (let i = 0; i < nodes.length; i++) {
          if (nodes[i].branch.id === targetId) {
            return { siblings: nodes, targetIndex: i };
          }
          // 递归查找
          if (nodes[i].children.length > 0) {
            const found = collectSiblings(nodes[i].children, targetId);
            if (found) return found;
          }
        }
        return null;
      };

      const result = collectSiblings(branchTree, targetBranch.id);
      if (!result) {
        setDraggingBranch(null);
        setDragTarget(null);
        return;
      }

      const { siblings, targetIndex } = result;

      // 找到拖拽分支在同级中的位置
      const draggedIndex = siblings.findIndex(n => n.branch.id === draggingBranch.id);

      // 创建新的排序列表
      const newOrder: Array<{ id: number; sort_order: number }> = [];
      let orderIndex = 0;

      if (draggedIndex === -1) {
        // 拖拽分支不在同级中，直接插入到目标位置
        for (let i = 0; i < siblings.length; i++) {
          if (i === targetIndex) {
            if (position === 'before') {
              newOrder.push({ id: draggingBranch.id, sort_order: orderIndex++ });
              newOrder.push({ id: siblings[i].branch.id, sort_order: orderIndex++ });
            } else {
              newOrder.push({ id: siblings[i].branch.id, sort_order: orderIndex++ });
              newOrder.push({ id: draggingBranch.id, sort_order: orderIndex++ });
            }
          } else {
            newOrder.push({ id: siblings[i].branch.id, sort_order: orderIndex++ });
          }
        }
      } else {
        // 拖拽分支在同级中，重新排序
        // 先移除拖拽分支
        const filteredSiblings = siblings.filter((_, i) => i !== draggedIndex);
        const adjustedTargetIndex = draggedIndex < targetIndex ? targetIndex - 1 : targetIndex;

        for (let i = 0; i < filteredSiblings.length; i++) {
          if (i === adjustedTargetIndex) {
            if (position === 'before') {
              newOrder.push({ id: draggingBranch.id, sort_order: orderIndex++ });
              newOrder.push({ id: filteredSiblings[i].branch.id, sort_order: orderIndex++ });
            } else {
              newOrder.push({ id: filteredSiblings[i].branch.id, sort_order: orderIndex++ });
              newOrder.push({ id: draggingBranch.id, sort_order: orderIndex++ });
            }
          } else {
            newOrder.push({ id: filteredSiblings[i].branch.id, sort_order: orderIndex++ });
          }
        }

        // 如果目标在最后
        if (adjustedTargetIndex >= filteredSiblings.length - 1 && position === 'after') {
          newOrder.push({ id: draggingBranch.id, sort_order: orderIndex });
        }
      }

      // 发送排序更新
      await reorderBranches(workspaceId, newOrder);

      // 刷新分支树
      await loadBranchTree();
    } catch (error) {
      console.error('分支排序失败:', error);
    } finally {
      setDraggingBranch(null);
      setDragTarget(null);
    }
  }, [draggingBranch, workspaceId, branchTree, reorderBranches, loadBranchTree]);

  // 加载状态
  if (loading && branchTree.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          {ICONS.loadingLarge}
          <p className="mt-2 text-sm text-gray-600">加载分支树...</p>
        </div>
      </div>
    );
  }

  // 错误状态
  if (error && branchTree.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center text-red-600">
          {ICONS.error}
          <p className="text-sm">{error}</p>
          <button
            onClick={handleRefresh}
            className="mt-3 px-4 py-2 text-sm bg-blue-500 hover:bg-blue-600 text-white rounded shadow-sm hover:shadow-md transition-all duration-150"
          >
            重试
          </button>
        </div>
      </div>
    );
  }

  // 空状态（无分支）
  if (branchTree.length === 0 && !loading) {
    return (
      <div className="h-full flex flex-col">
        {showToolbar && (
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-medium text-gray-900">分支树</h2>
            <button
              onClick={handleRefresh}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
              title="刷新"
            >
              {ICONS.refresh}
            </button>
          </div>
        )}

        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            {ICONS.empty}
            <p className="text-sm text-gray-600 mb-1">暂无分支</p>
            <p className="text-xs text-gray-500">在章节末尾创建分支点以开启多线剧情</p>
          </div>
        </div>
      </div>
    );
  }

  // 正常状态
  return (
    <div className="h-full flex flex-col">
      {/* 顶部工具栏 */}
      {showToolbar && (
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900">分支树</h2>
          <div className="flex items-center gap-2">
            <button
              onClick={handleExpandAll}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
              title="展开全部"
            >
              {ICONS.expandAll}
            </button>
            <button
              onClick={handleCollapseAll}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
              title="折叠全部"
            >
              {ICONS.collapseAll}
            </button>
            <button
              onClick={handleRefresh}
              className="p-2 text-gray-500 hover:text-gray-700 transition-colors"
              title="刷新"
            >
              {ICONS.refresh}
            </button>
          </div>
        </div>
      )}

      {/* 分支树内容 */}
      <div className="flex-1 overflow-y-auto p-4 space-y-1">
        {/* 主线节点 */}
        <MainLineNode
          isSelected={selectedBranchId === null}
          isCurrentBranch={isMainLine}
          onClick={handleMainLineClick}
          onDoubleClick={handleMainLineDoubleClick}
        />

        {/* 分支节点 */}
        {branchTree.map((node) => (
          <BranchNode
            key={node.branch.id}
            node={node}
            depth={0}
            onBranchClick={handleBranchClick}
            onBranchSwitch={handleBranchSwitch}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            isDragging={draggingBranch !== null}
            dragTarget={dragTarget}
          />
        ))}
      </div>

      {/* 加载指示器 */}
      {loading && branchTree.length > 0 && (
        <div className="p-4 text-center">
          {ICONS.loadingSmall}
        </div>
      )}

      {/* 底部统计信息 */}
      {!loading && (
        <div className="p-4 border-t border-gray-200 text-center text-xs text-gray-600">
          共 {branchTree.length} 个分支点
          {draggingBranch && <span className="ml-2 text-blue-500">· 拖拽排序中...</span>}
        </div>
      )}
    </div>
  );
};

export default BranchTree;